import { useEffect, useState, useRef } from 'react';
import { connect } from 'redux-bundler-react';

import { MapContainer, TileLayer, FeatureGroup, LayersControl, GeoJSON, useMap } from 'react-leaflet';

import * as turf from '@turf/turf';

import { EditControl } from 'react-leaflet-draw';
import './map.scss';
import { useFormContext } from 'react-hook-form';
import Geocoder from './components/geocoder';
import ResetMap from './components/ResetMap';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import 'leaflet-sleep';
import FullscreenControl from './components/fullScreenControl';
import statusModal from '@forms/components/modals/statusModal';
import { useModal, usePlss, useRequest } from '@stores';

import { formatCoordStr, formatCoordFlt } from '@src/utils/helpers';
import { useReverseGeocode } from '@hooks';
import { leafletAttribution } from './helper';

//CUSTOM LEAFLET FIXES
import { AorQueries } from '@api/services/aor';
import { ErrorMessages } from '@src/utils/enums';

const customizePolyHandle = (prototype) => {
  var options = prototype.options;
  options.icon.options.iconSize = new L.Point(10, 10);
  options.touchIcon = options.icon;
};

const customizeSimpleShapeHandle = (prototype) => {
  var options = prototype.options;
  options.moveIcon.options.iconSize = new L.Point(10, 10);
  options.resizeIcon = options.moveIcon;
  options.touchMoveIcon = options.moveIcon;
  options.touchResizeIcon = options.moveIcon;
};

customizePolyHandle(L.Draw.Polyline.prototype);
customizePolyHandle(L.Edit.PolyVerticesEdit.prototype);
customizeSimpleShapeHandle(L.Edit.SimpleShape.prototype);

const DefaultIcon = L.icon({
  iconUrl: icon,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  shadowAnchor: [4, 62],
  popupAnchor: [-3, -76],
});

L.Marker.prototype.options.icon = DefaultIcon;
L.drawLocal.draw.toolbar.buttons.polyline = 'Add a polyline feature';
L.drawLocal.draw.toolbar.buttons.marker = 'Add a point feature';
L.drawLocal.draw.toolbar.buttons.polygon = 'Add a polygon feature';
L.drawLocal.edit.toolbar.actions.clearAll = { title: 'Remove All', text: 'Remove All' };
L.drawLocal.edit.toolbar.actions.save = { title: 'Finish', text: 'Finish' };

const MyCustomMarker = L.Icon.extend({
  options: {
    iconUrl: icon,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    shadowAnchor: [4, 62],
    popupAnchor: [-3, -76],
  },
});

const zoom = 4;
const center = [37.612345, -95.661234];

const Map = connect('doUpdateRequestLocation', ({ doUpdateRequestLocation, setProjectOutsideUS, height = '60vh' }) => {
  const { openSecondaryModal } = useModal();
  const { setPlssGeometry } = usePlss();

  const [geometry, setGeometry] = useState();
  const [centroid, setCentroid] = useState();
  const [extent, setExtent] = useState();
  const [showReset, setShowReset] = useState(false);

  const mapRef = useRef();
  const featureGroupRef = useRef();
  const layersControlRef = useRef();

  const { setValue, clearErrors } = useFormContext();
  const {
    isFormReadOnly: isReadOnly,
    projectBounds,
    projectGeometry,
    setProjectBounds,
    setProjectGeometry,
  } = useRequest();

  //Get Address from centroid Lat/Lng
  useReverseGeocode(centroid, false);

  const handleReset = () => {
    const map = mapRef.current;
    setShowReset(false);
    //remove search marker
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker && layer.options.customId === 'search-marker') {
        map.removeLayer(layer); // Remove the marker from the map
      }
    });
    projectBounds ? setExtent(projectBounds) : map.setView(center).setZoom(zoom);
  };

  const handleObjectCreation = async (e) => {
    const newGeoJson = e.layer.toGeoJSON();
    const geometry = newGeoJson.geometry;

    const isWithinValidDistrict = await AorQueries.isGeometryInValidDistrict(geometry);

    if (isWithinValidDistrict) {
      clearErrors('mapScroll');
      switch (e.layerType) {
        case 'marker':
          //Calculate Centroid
          const markerCentroid = e.layer.getLatLng();
          const point = turf.point([markerCentroid.lng, markerCentroid.lat]);
          const radius = turf.buffer(point, 5, { units: 'miles' });
          const bbox = turf.bbox(radius);
          const bounds = [
            [bbox[1], bbox[0]],
            [bbox[3], bbox[2]],
          ];
          const markerFixedCentroid = {
            lat: formatCoordStr(markerCentroid.lat),
            lng: formatCoordStr(markerCentroid.lng),
          };
          const reversePointCentroidGeometry = {
            type: 'Point',
            coordinates: [markerCentroid.lng, markerCentroid.lat],
          };
          if (Math.sign(markerCentroid.lat) === -1 || Math.sign(markerCentroid.lng) === 1) {
            setProjectOutsideUS(true);
          } else {
            setProjectOutsideUS(false);
          }
          setValue('latitude', markerFixedCentroid.lat, { shouldValidate: true });
          setValue('longitude', markerFixedCentroid.lng, { shouldValidate: true });
          doUpdateRequestLocation({
            latitude: formatCoordFlt(markerCentroid.lat),
            longitude: formatCoordFlt(markerCentroid.lng),
          });
          setProjectBounds(bounds);
          setExtent(point);

          setCentroid(`${formatCoordStr(markerCentroid.lng)},${formatCoordStr(markerCentroid.lat)}`);
          setGeometry(reversePointCentroidGeometry);
          setPlssGeometry(reversePointCentroidGeometry);
          doUpdateRequestLocation({ geometry: reversePointCentroidGeometry });
          setProjectGeometry(reversePointCentroidGeometry);
          e.target.removeLayer(e.layer);
          return;
        case 'polygon':
          //Calculate Centroid
          const polygonCentroid = turf.centerOfMass(geometry);
          const polygonCentroidLat = polygonCentroid.geometry.coordinates[1];
          const polygonCentroidLng = polygonCentroid.geometry.coordinates[0];
          const polygonFixedCentroid = {
            lat: formatCoordStr(polygonCentroidLat),
            lng: formatCoordStr(polygonCentroidLng),
          };
          setCentroid(`${formatCoordStr(polygonCentroidLng)},${formatCoordStr(polygonCentroidLat)}`);
          if (Math.sign(polygonCentroidLat) === -1 || Math.sign(polygonCentroidLng) === 1) {
            setProjectOutsideUS(true);
          } else {
            setProjectOutsideUS(false);
          }
          doUpdateRequestLocation({
            latitude: formatCoordFlt(polygonCentroidLat),
            longitude: formatCoordFlt(polygonCentroidLng),
          });
          setValue('latitude', polygonFixedCentroid.lat, { shouldValidate: true });
          setValue('longitude', polygonFixedCentroid.lng, { shouldValidate: true });

          //Calculate Project Area (Acres)
          const boundary = e.layer.getLatLngs()[0];
          const area = L.GeometryUtil.geodesicArea(boundary);
          const formattedArea = area / 4046.856422;
          const roundedArea = formattedArea.toFixed(6);
          doUpdateRequestLocation({ projectArea: Number(roundedArea), unitOfMeasurement: 'Acres' });
          setValue('projectArea', roundedArea, { shouldValidate: true });
          setValue('unitOfMeasurement', 'Acres', { shouldValidate: true });

          const polygonBounds = e.layer.getBounds();
          const polygonBoundsFormatted = [
            [polygonBounds._northEast.lat, polygonBounds._northEast.lng],
            [polygonBounds._southWest.lat, polygonBounds._southWest.lng],
          ];
          setProjectBounds(polygonBoundsFormatted);
          setExtent(polygonBoundsFormatted);
          setGeometry(geometry);
          setPlssGeometry(geometry);
          doUpdateRequestLocation({ geometry: geometry });
          setProjectGeometry(geometry);

          e.target.removeLayer(e.layer);
          return;
        case 'polyline':
          //Calculate Centroid
          const lineLength = turf.length(geometry, { units: 'kilometers' });
          const lineCentroid = turf.along(geometry, lineLength / 2, { units: 'kilometers' });
          const lineCentroidLat = lineCentroid.geometry.coordinates[1];
          const lineCentroidLng = lineCentroid.geometry.coordinates[0];
          const lineFixedCentroid = {
            lat: formatCoordStr(lineCentroidLat),
            lng: formatCoordStr(lineCentroidLng),
          };
          const lineBounds = e.layer.getBounds();
          const lineBoundsFormatted = [
            [lineBounds._northEast.lat, lineBounds._northEast.lng],
            [lineBounds._southWest.lat, lineBounds._southWest.lng],
          ];
          setCentroid(`${formatCoordStr(lineCentroidLng)},${formatCoordStr(lineCentroidLat)}`);
          if (Math.sign(lineCentroidLat) === -1 || Math.sign(lineCentroidLng) === 1) {
            setProjectOutsideUS(true);
          } else {
            setProjectOutsideUS(false);
          }
          doUpdateRequestLocation({
            latitude: formatCoordFlt(lineCentroidLat),
            longitude: formatCoordFlt(lineCentroidLng),
          });

          setValue('latitude', lineFixedCentroid.lat, { shouldValidate: true });
          setValue('longitude', lineFixedCentroid.lng, { shouldValidate: true });
          setProjectBounds(lineBoundsFormatted);
          setExtent(lineBoundsFormatted);
          setGeometry(geometry);
          setPlssGeometry(geometry);
          doUpdateRequestLocation({ geometry: geometry });
          setProjectGeometry(geometry);
          e.target.removeLayer(e.layer);
          return;
        default:
          return;
      }
    } else {
      if (isWithinValidDistrict === false) {
        openSecondaryModal(statusModal, {
          msg: ErrorMessages.InvalidDistrict,
          status: 'Invalid Geometry Location',
        });
      }
      e.target.removeLayer(e.layer);
    }
  };

  useEffect(() => {
    if (!geometry && projectGeometry && projectBounds) {
      setGeometry(projectGeometry);
      setPlssGeometry(projectGeometry);
      setExtent(projectBounds);
    }
  }, [projectBounds, projectGeometry, geometry, setGeometry, setExtent, setPlssGeometry]);

  const handleObjectDeletion = () => {
    doUpdateRequestLocation({ geometry: {} });
    setGeometry(null);
    setPlssGeometry(null);
    setProjectGeometry(null);
    setValue('latitude', null);
    setValue('longitude', null);
    setValue('projectArea', null);
    setValue('district', null);
    setValue('plssSection', null);
    setValue('plssTownship', null);
    setValue('plssRange', null);
    doUpdateRequestLocation({});
  };

  const PanToExtent = () => {
    const map = useMap();

    useEffect(() => {
      map?.invalidateSize();
      if (extent?.length > 0) {
        map?.fitBounds(extent);
      }
    }, [map]);

    return null;
  };

  //Fix to leaflet rendering bug
  const RefreshMap = () => {
    const map = useMap();
    useEffect(() => {
      map.attributionControl.setPrefix(leafletAttribution);
      map.invalidateSize();
    }, [map]);

    return null;
  };

  const MyGeoJSONData = () => {
    const [data, setData] = useState();

    useEffect(() => {
      geometry && setData(geometry);
    }, []);

    return data ? (
      <GeoJSON
        data={data}
        onEachFeature={(feature, layer) => {
          layer.bindTooltip('Project Boundary', {
            direction: 'right',
            permanent: false,
            sticky: true,
            offset: [10, 0],
            opacity: 0.75,
          });
        }}
      />
    ) : null;
  };

  return (
    <div style={{ display: 'flex', height: height }}>
      <MapContainer
        style={{ flex: 1, width: '100%' }}
        center={center}
        zoom={zoom}
        ref={mapRef}
        sleepNote={false}
        hoverToWake={false}
        sleepOpacity={1}
        worldCopyJump={true}
      >
        <FullscreenControl />
        <Geocoder resultZoomLevel={19} setShowReset={setShowReset} />
        <LayersControl ref={layersControlRef} position='bottomright'>
          <LayersControl.BaseLayer checked name='OSM Streets'>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name='ESRI World Imagery'>
            <TileLayer
              attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
              url='https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name='USGS World Imagery (zoom limit)'>
            <TileLayer
              attribution='Tiles courtesy of the <a href="https://usgs.gov/">U.S. Geological Survey</a>'
              url='https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}'
              maxZoom={16}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name='USGS Topographic (zoom limit)'>
            <TileLayer
              attribution='Tiles courtesy of the <a href="https://usgs.gov/">U.S. Geological Survey</a>'
              url='https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile/{z}/{y}/{x}'
              maxZoom={16}
            />
          </LayersControl.BaseLayer>
        </LayersControl>
        <FeatureGroup ref={featureGroupRef}>
          {!isReadOnly && (
            <EditControl
              position='topleft'
              onCreated={(e) => handleObjectCreation(e)}
              onDeleted={(e) => handleObjectDeletion(e)}
              draw={{
                style: { zIndex: 200 },
                marker: { icon: new MyCustomMarker() },
                polyline: {
                  shapeOptions: { weight: 2 },
                  showLength: true,
                  metric: false,
                  feet: false,
                },
                polygon: {
                  shapeOptions: { weight: 2 },
                  showArea: true,
                  showLength: true,
                  metric: false,
                  feet: false,
                },
                rectangle: false,
                circle: false,
                circlemarker: false,
              }}
              edit={{ edit: false }}
            />
          )}
          <PanToExtent />
          <MyGeoJSONData />
        </FeatureGroup>
        <RefreshMap />
        {showReset && <ResetMap handleReset={handleReset} />}
      </MapContainer>
    </div>
  );
});

export default Map;
